// @ts-nocheck
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Checkbox, GlobalSummaryBox, Loader, LoadingSkeleton } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { isToday } from '@/helpers/reporting.helpers';
import {
  getAnalyticalView,
  getSectionsVisibility,
  getSelectedCampaigns,
  getStaticMode
} from '@/store/selectors/reporting.selectors';
import { getCampaigns, getTimeFrame } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { useSummaryContext } from '@/ts-contexts/reporting/Summary/SummaryContext';
import { useCampaignSummary } from '@/ts-contexts/reporting/Summary/useCampaignSummary';
import { SectionHeader } from '@/ts-modules/reporting/SectionHeader';
import { SummaryCampaignItemDetails } from '@/ts-modules/reporting/Summary/SummaryCampaignItemDetails';
import { SummaryDownload } from './SummaryDownload';

import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';
import type { FilterSection } from '@/ts-types/reporting.types';

type Props = {
  filterSection: FilterSection;
};

export function Summary({ filterSection }: Props): React.ReactElement {
  const { formatMessage } = useIntl();
  const { summaryItemsKeys, summaryStatsReady } = useSummaryContext();

  // @ts-ignore
  const timeFrame = useSelector(store => getTimeFrame(store, filterSection));
  const staticMode = useSelector(getStaticMode);
  // @ts-ignore
  const analyticalView = useSelector(store => getAnalyticalView(store, filterSection));
  const campaignIds: string[] = useSelector(getCampaigns);
  const reportingCampaigns = useSelector(getSelectedCampaigns);
  const campaigns: Record<string, any>[] = reportingCampaigns.filter(({ id }: { id: string }) =>
    campaignIds.includes(id)
  );
  const userRoles: UserRoleInterface[] = useSelector(getUserRoles);
  const user: UserInterface = useSelector(getCurrentUser);
  const sectionsVisibility = useSelector(getSectionsVisibility);

  const showAllLinesPermissions = checkUserResourcePermissions('reportingShowSummaryLines', user, userRoles);
  const showAnalyticalViewPermissions =
    checkUserResourcePermissions('reportingShowAnalyticalViewSummaryBoxes', user, userRoles) && !staticMode;
  const campaignItemDetailsPermissions = checkUserResourcePermissions('reportingCampaignItemDetails', user, userRoles);

  const [{ analyticalViewIsActive, campaignLines }, setState] = useState({
    analyticalViewIsActive: false,
    campaignLines: {
      status: 'disabled'
    }
  });

  const showCampaignLines = useCallback(() => {
    const { status } = campaignLines;
    setState(prevState => ({
      ...prevState,
      campaignLines: {
        status: status === 'enabled' ? 'disabled' : 'enabled'
      }
    }));
  }, [campaignLines]);

  const showAnalyticalView = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      analyticalViewIsActive: !analyticalViewIsActive
    }));
  }, [analyticalViewIsActive]);

  const switchIsActive = campaignLines.status === 'enabled';
  const numOfBoxes = summaryItemsKeys?.length;
  const showAnalyticalViewSwitch = useMemo(() => {
    const end = moment.utc(timeFrame.endDate).endOf('day');
    let differenceInDays = moment(end).diff(moment.utc(timeFrame.beginDate), 'days');
    differenceInDays += isToday(timeFrame.endDate) ? 0 : 1;
    return differenceInDays >= 2;
  }, [analyticalView]);

  const summary = useCampaignSummary({
    filterSection,
    showAnalyticalView: analyticalViewIsActive,
    showCampaignsBreakdowns: switchIsActive
  });

  return (
    <section
      className="reportingOverview"
      style={{ display: !sectionsVisibility.reportingOverview ? 'none' : 'block' }}
    >
      <div
        className={`reportingShortcuts__element--${campaignIds.length > 1 ? 'multi' : 'single'}`}
        id="reportingOverview"
      />
      <SectionHeader downloadButton={<SummaryDownload />} hideAdvancedFilters={!!staticMode} section={filterSection}>
        <div className="reportingOverview__header">
          <div className="h2 fontRegular reportingOverviewTitle">
            {formatMessage({
              defaultMessage: 'Campaign Summary',
              id: 'reporting.summary.title'
            })}
          </div>
          {!summaryStatsReady ? (
            <LoadingSkeleton count={4} height={10} width="100%" />
          ) : (
            <>
              {campaigns.length > 1 && showAllLinesPermissions ? (
                <Checkbox
                  active={switchIsActive}
                  classes="reportingOverviewSwitch"
                  id="show-campaign-lines"
                  isSwitch
                  labelColor="#505969"
                  labelText={formatMessage({
                    defaultMessage: 'Show campaign lines',
                    id: 'reporting.summary.switch'
                  })}
                  onClickAction={() => showCampaignLines()}
                />
              ) : null}
              {showAnalyticalViewPermissions && showAnalyticalViewSwitch && (
                <Checkbox
                  active={analyticalViewIsActive}
                  classes="reportingOverviewAnalyticalSwitch"
                  disabled={isEmpty(analyticalView)}
                  id="show-analytical-view"
                  isSwitch
                  labelColor="#505969"
                  labelText={formatMessage({
                    defaultMessage: 'Analytical view',
                    id: 'reporting.summary.analyticalSwitch'
                  })}
                  onClickAction={showAnalyticalView}
                />
              )}
            </>
          )}
        </div>
      </SectionHeader>
      <div className="reportingOverview__container">
        {!summary.isReady ? (
          <LoadingSkeleton count={4} height={40} width="100%" />
        ) : (
          <div
            className={`reportingOverview__boxes numOfBoxes${numOfBoxes}
            ${switchIsActive ? 'campaignLinesEnable' : 'campaignLinesDisabled'}`}
          >
            {summary.items?.map(item => (
              <div className={`reportingOverviewItem boxPanel ${item.itemClassName}`} key={item.key}>
                {!item.summaryItem && (
                  <div className="overviewItemLoader">
                    <Loader showBackground type="landing" variant="default" />
                  </div>
                )}
                <GlobalSummaryBox
                  bottomBoxConfig={item.bottom}
                  footerConfig={item.footerConfig}
                  key={`reportingOverviewItem_globalSummary_${switchIsActive}`}
                  middleBoxConfigs={item.middle}
                  middleContentDirection={switchIsActive ? 'line' : 'column'}
                  topBoxConfig={item.top}
                />
              </div>
            ))}
          </div>
        )}
        {campaignItemDetailsPermissions && campaigns.length > 1 && (
          <SummaryCampaignItemDetails filterSection="summary" />
        )}
      </div>
    </section>
  );
}
